'use client'

import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Bell } from 'lucide-react'

import { UserModal } from '../UserProfile/Modal'

import type { FeedItem, FeedItemNotificationResource } from '@/features/profiles/types/feed'
import { SocialFeedActionBlock } from '@/features/social/components/SocialFeedActionBlock'
import { useModalContext } from '@/shared/contexts/Modal'
import { useIsCurrentViewer } from '@/shared/hooks/useCurrentUserUid'
import { useUsersAnalytics } from '@/shared/lib/analytics'
import { urlsFromUserCosmetics } from '@/shared/lib/user-feed-avatar-urls'
import { AvatarComposite } from '@/shared/ui/AvatarComposite'
import {
  FeedActivityAccentWell,
  FeedCardSurface
} from '@/shared/ui/FeedCardSurface'

interface NotificationFeedItemProps {
  item: FeedItem
  resource: FeedItemNotificationResource
  showActions?: boolean
}

export function NotificationFeedItem({
  item,
  resource,
  showActions = true
}: NotificationFeedItemProps) {
  const imageUrl = resource.image?.file?.url
  const linkUrl = resource.link_url || undefined
  const linkTitle = resource.link_title || 'Abrir link'
  const { openModal } = useModalContext()
  const itsMe = useIsCurrentViewer(item.from.uid)
  const { trackUserFeedInteraction } = useUsersAnalytics()
  const avatarUrls = urlsFromUserCosmetics(item.from)

  const openActorProfile = () => {
    trackUserFeedInteraction('clicked', item.from.uid)
    openModal({ modal: UserModal }, { userUid: item.from.uid })
  }

  return (
    <FeedCardSurface density="compact">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={openActorProfile}
          className="shrink-0 rounded-lg outline-none ring-offset-2 ring-offset-[#252538] transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-jacaranda"
          aria-label={itsMe ? 'Abrir seu perfil' : `Perfil de ${item.from.name}`}
        >
          <AvatarComposite
            {...avatarUrls}
            size="xs"
            className="pointer-events-none"
          />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-start justify-between gap-1.5">
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight tracking-tight text-white">
                {itsMe ? 'Você' : item.from.name}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-[#7F7FAD]">
                <span>
                  {formatDistanceToNow(new Date(item.created_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </span>
                <span className="text-white/20" aria-hidden>
                  ·
                </span>
                <span className="font-medium text-[#be64e3]">Aviso</span>
              </p>
            </div>
            <FeedActivityAccentWell size="sm" className="rounded-md">
              <Bell
                className="size-4 text-[#fbe576] drop-shadow-[0_0_8px_rgba(251,229,118,0.3)]"
                strokeWidth={2}
                aria-hidden
              />
            </FeedActivityAccentWell>
          </div>
          <div className="flex flex-col gap-1.5 pt-1.5">
            <h2 className="text-[13px] font-semibold leading-snug text-white">
              {resource.subject}
            </h2>
            {resource.subtitle ? (
              <p className="text-xs font-medium text-primary-foreground">
                {resource.subtitle}
              </p>
            ) : null}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={resource.subject}
                className="max-h-32 w-full rounded-lg object-cover ring-1 ring-inset ring-white/10"
              />
            ) : null}
            <p className="text-xs leading-relaxed text-[#c8c8dc] whitespace-pre-wrap">
              {resource.content}
            </p>
            {linkUrl ? (
              <a
                href={linkUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-primary-foreground underline decoration-primary-foreground/40 underline-offset-2 transition-colors hover:decoration-primary-foreground"
              >
                {linkTitle}
              </a>
            ) : null}
          </div>
        </div>
      </div>
      {showActions && <SocialFeedActionBlock item={item} compact />}
    </FeedCardSurface>
  )
}
